import styles from './accordionItem.module.scss';
import {
  MouseEvent,
  memo,
  useEffect,
  useRef,
  useState
} from "react";
import { CSSTransition } from "react-transition-group";

type AccordionItemProps = {
  title: JSX.Element;
  child: JSX.Element;
  isOpen: boolean;
  onClick: () => void;
  activeClassName?: string;
  customClick?: boolean;
  maxHeight?: number;
  disabledAutoScroll?: boolean;
}

export const AccordionItem = memo(({ title, child, isOpen, onClick, activeClassName, maxHeight, customClick, disabledAutoScroll }: AccordionItemProps) => {

  const answerContainer = useRef<HTMLDivElement>(null);
  const itemContainer = useRef<HTMLDivElement>(null);
  const contentHeight = useRef<HTMLDivElement>(null);
  const isMouseDownOnInteractiveElement = useRef(false);

  const [scrollHeight, setScrollHeight] = useState<number>();

  useEffect(() => {
    if (contentHeight.current) {
      const observer = new ResizeObserver(() => {
        setScrollHeight(contentHeight.current?.scrollHeight);
      });
      observer.observe(contentHeight.current);
      return () => observer.disconnect();
    }
  }, [child, isOpen]);

  const autoScroll = () => {
    const container = itemContainer.current;
    const parentScroll = getScrollParent(container?.parentElement); // parent element, for good working, need have relative position

    if (!!parentScroll && !!container) {
      if (container.clientHeight < parentScroll.clientHeight) {
        const calculate = container.clientHeight + container.offsetTop - parentScroll.scrollTop;
        if (calculate < container.clientHeight || calculate > parentScroll.clientHeight) {
          smoothScrollTo(parentScroll, container.offsetTop, 300);
        }
      } else {
        smoothScrollTo(parentScroll, container.offsetTop, 300);
      }
    }
  }

  const smoothScrollTo = (element: HTMLElement, scrollTop: number, duration: number) => {
    let start = element.scrollTop;
    let change = scrollTop - start;
    let startTime = performance.now();

    function animateScroll(currentTime: number) {
      let timeElapsed = currentTime - startTime;
      let progress = Math.min(timeElapsed / duration, 1);
      element.scrollTop = start + change * easeInOutQuad(progress);

      if (timeElapsed < duration) {
        requestAnimationFrame(animateScroll);
      }
    }

    function easeInOutQuad(t: number) {
      return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    }

    requestAnimationFrame(animateScroll);
  }

  const getScrollParent = (node?: HTMLElement | null): HTMLElement | null =>  {
    if (node == null) {
      return null;
    }

    if (node.scrollHeight > node.clientHeight) {
      return node;
    } else {
      return getScrollParent(node.parentElement);
    }
  }

  const isInteractiveElement = (element: EventTarget | null): boolean => {
    return element instanceof HTMLElement && !!element.closest('input, textarea, select, button, a, [role="button"]');
  }

  const hasSelectedInputText = (): boolean => {
    const element = document.activeElement;

    return (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement)
      && element.selectionStart !== null
      && element.selectionEnd !== null
      && element.selectionStart !== element.selectionEnd;
  }

  const handleTitleMouseDownCapture = (event: MouseEvent<HTMLDivElement>) => {
    isMouseDownOnInteractiveElement.current = isInteractiveElement(event.target);
  }

  const handleTitleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (isInteractiveElement(event.target) || isMouseDownOnInteractiveElement.current || hasSelectedInputText()) {
      isMouseDownOnInteractiveElement.current = false;
      return;
    }

    isMouseDownOnInteractiveElement.current = false;
    onClick();
  }

  return (
    <div className={styles.container} ref={itemContainer}>
      <div
        className={`${styles.title} ${isOpen ? activeClassName : ''}`}
        onClick={!customClick ? handleTitleClick : undefined}
        onMouseDownCapture={!customClick ? handleTitleMouseDownCapture : undefined}
      >
        {title}
      </div>

      <CSSTransition in={isOpen}
                     timeout={300}
                     unmountOnExit
                     nodeRef={answerContainer}
                     onEntered={() => !disabledAutoScroll && autoScroll()}
                     classNames={{
                       enter: styles.enter,
                       exitActive: styles.exitActive,
                     }}
      >
        <div className={styles.answerContainer} ref={answerContainer} style={{
          height: isOpen ? scrollHeight : 0,
          maxHeight: maxHeight
        }}>
          <div
            ref={contentHeight}
            className={styles.answerInner}
            style={{
              maxHeight: maxHeight,
            }}>
            {child}
          </div>
        </div>
      </CSSTransition>
    </div>
  )
})
